import { supabase } from '../lib/supabase';
import { DEMO_ERROR_MESSAGE, isDemoUser } from '../utils/demoMode';

export const drawingService = {
  async uploadDrawing(file, drawingData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Demo mode guard — surface an explicit error instead of fake success.
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }

      const fileExt = file?.name?.split('.')?.pop();
      const safeDrawingNumber = (drawingData?.drawingNumber || 'no-number')?.replace(/[^a-zA-Z0-9-_]/g, '_');
      const safeRevision = (drawingData?.revision || 'R0')?.replace(/[^a-zA-Z0-9-_]/g, '_');
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${drawingData?.siteId}/${safeDrawingNumber}/${safeRevision}/${fileName}`;

      const { error: uploadError } = await supabase?.storage
        ?.from('site-drawings')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        // Translate Supabase storage 413 to an actionable message.
        const statusCode = String(uploadError?.statusCode ?? '');
        const msgStr = String(uploadError?.message ?? '');
        if (statusCode === '413' || /exceeded.*size|too large|payload too large/i.test(msgStr)) {
          const friendly = new Error('Drawing file is too large for the site-drawings bucket.');
          friendly.code = 'PAYLOAD_TOO_LARGE';
          friendly.statusCode = 413;
          friendly.cause = uploadError;
          throw friendly;
        }
        throw uploadError;
      }

      // Insert new drawing as 'current' — the DB trigger will supersede previous revisions
      const { data, error } = await supabase?.from('site_drawings')
        ?.insert({
          site_id: drawingData?.siteId,
          company_id: drawingData?.companyId,
          drawing_name: drawingData?.drawingName,
          drawing_number: drawingData?.drawingNumber || null,
          revision: drawingData?.revision || null,
          discipline: drawingData?.discipline || null,
          description: drawingData?.description || null,
          file_path: filePath,
          file_size: file?.size,
          mime_type: file?.type,
          version_number: drawingData?.versionNumber || 1,
          is_latest: true,
          drawing_status: 'current',
          uploaded_by: user?.id
        })
        ?.select('*, uploaded_by_profile:user_profiles!site_drawings_uploaded_by_fkey(full_name, email)');

      if (error) throw error;
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async getDrawingsBySite(siteId) {
    try {
      const { data, error } = await supabase?.from('site_drawings')
        ?.select('*, uploaded_by_profile:user_profiles!site_drawings_uploaded_by_fkey(full_name, email)')
        ?.eq('site_id', siteId)
        ?.order('upload_date', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getCurrentDrawings(siteId) {
    try {
      const { data, error } = await supabase?.from('site_drawings')
        ?.select('*, uploaded_by_profile:user_profiles!site_drawings_uploaded_by_fkey(full_name, email)')
        ?.eq('site_id', siteId)
        ?.eq('drawing_status', 'current')
        ?.order('drawing_name', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getLatestDrawings(siteId) {
    try {
      const { data, error } = await supabase?.from('site_drawings')
        ?.select('*, uploaded_by_profile:user_profiles!site_drawings_uploaded_by_fkey(full_name, email)')
        ?.eq('site_id', siteId)
        ?.eq('drawing_status', 'current')
        ?.order('drawing_name', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getAllDrawingsBySite(siteId) {
    try {
      const { data, error } = await supabase?.from('site_drawings')
        ?.select('*, uploaded_by_profile:user_profiles!site_drawings_uploaded_by_fkey(full_name, email)')
        ?.eq('site_id', siteId)
        ?.order('drawing_number', { ascending: true })
        ?.order('upload_date', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getDrawingVersions(siteId, drawingNumber) {
    try {
      const { data, error } = await supabase?.from('site_drawings')
        ?.select('*, uploaded_by_profile:user_profiles!site_drawings_uploaded_by_fkey(full_name, email)')
        ?.eq('site_id', siteId)
        ?.eq('drawing_number', drawingNumber)
        ?.order('upload_date', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getDrawingById(drawingId) {
    try {
      const { data, error } = await supabase?.from('site_drawings')
        ?.select('*, sites(site_name, address_line1, city, postcode), uploaded_by_profile:user_profiles!site_drawings_uploaded_by_fkey(full_name, email)')
        ?.eq('id', drawingId)
        ?.single();

      if (error) throw error;
      return { data: data || null, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async getDrawingFileUrl(filePath) {
    try {
      // Try site-drawings bucket first
      const { data, error } = await supabase?.storage
        ?.from('site-drawings')
        ?.createSignedUrl(filePath, 3600);

      if (!error && data?.signedUrl) {
        return { url: data?.signedUrl, error: null };
      }

      // Fallback: try site-documents bucket (used for seeded demo drawings)
      const { data: data2, error: error2 } = await supabase?.storage
        ?.from('site-documents')
        ?.createSignedUrl(filePath, 3600);

      if (error2) throw error2;
      return { url: data2?.signedUrl, error: null };
    } catch (error) {
      return { url: null, error: { message: error?.message } };
    }
  },

  async deleteDrawing(drawingId, filePath) {
    try {
      const { error: storageError } = await supabase?.storage
        ?.from('site-drawings')
        ?.remove([filePath]);

      if (storageError) throw storageError;

      const { error } = await supabase?.from('site_drawings')
        ?.delete()
        ?.eq('id', drawingId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: { message: error?.message } };
    }
  }
};